import { motion } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Scales, Brain, ShieldCheck, GraduationCap, Handshake, Lightning, Microphone, Certificate, Rocket, LockKey, BookOpen, Globe } from '@phosphor-icons/react';

const Hero = () => {
  const achievements = [
    { text: '30+ Años Experiencia', icon: Scales },
    { text: 'Experto en IA Jurídica', icon: Brain },
    { text: 'Autor de 3 Libros', icon: BookOpen },
    { text: 'Ciberseguridad', icon: LockKey },
    { text: 'Derecho Digital', icon: Globe },
    { text: 'Formador & Docente', icon: GraduationCap },
    { text: 'Fundador Jurisciberia', icon: Rocket },
    { text: 'Compliance Officer', icon: Certificate },
    { text: 'Transformación Digital', icon: Lightning },
    { text: 'Innovación Legal', icon: Brain },
    { text: 'Defensa Ciberdelitos', icon: ShieldCheck },
    { text: 'Legal Tech', icon: Globe },
    { text: 'Negociación Experta', icon: Handshake },
    { text: 'Ponente Internacional', icon: Microphone },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.6, ease: 'easeOut' } }
  };

  return (
    <section id="home" className="min-h-[85vh] bg-[#050505] grid-lines relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-600/5 via-transparent to-transparent pointer-events-none" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-6">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          {/* Left: Text Content */}
          <div className="space-y-8">
            <motion.p 
              variants={itemVariants}
              className="font-mono text-xs tracking-[0.2em] uppercase text-blue-500"
              data-testid="hero-label"
            >
              [01] Abogado / IA / Ciberseguridad
            </motion.p>
            
            <motion.h1 
              variants={itemVariants}
              className="text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tighter text-white leading-[1.1]"
              data-testid="hero-title"
            >
              José Luis
              <br />
              <span className="text-white/90">Vilaplana</span>
              <br />
              <span className="text-blue-500">Villajos</span>
            </motion.h1>

            <motion.p 
              variants={itemVariants}
              className="text-xl text-white/65 leading-relaxed max-w-lg"
              data-testid="hero-subtitle"
            >
              Abogado innovador con 30+ años de experiencia liderando la 
              <span className="text-white"> transformación digital </span> 
              del sector jurídico español.
            </motion.p>

            <motion.div variants={itemVariants} className="flex items-center gap-6">
              <a
                href="https://calendar.app.google/8cNXBV981iJPzp4W9"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border border-blue-500 text-blue-500 font-medium hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-300"
                data-testid="hero-cta-videocita"
              >
                Agendar Videocita
              </a>
              <a
                href="#projects"
                className="text-white/65 hover:text-white transition-colors font-medium"
                data-testid="hero-cta-projects"
              >
                Ver Proyectos
              </a>
            </motion.div>

            <motion.p 
              variants={itemVariants}
              className="font-mono text-sm text-blue-500"
              data-testid="hero-handle"
            >
              @tecnoabogado
            </motion.p>
          </div>

          {/* Right: Portrait */}
          <motion.div 
            variants={itemVariants}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative">
              <div className="w-72 h-72 sm:w-80 sm:h-80 lg:w-96 lg:h-96 border border-white/10 p-4">
                <div className="w-full h-full overflow-hidden relative bg-[#0F0F0F]">
                  <img
                    src="/jose-luis-vilaplana.jpg"
                    alt="José Luis Vilaplana Villajos"
                    className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                    data-testid="hero-portrait"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full items-center justify-center bg-gradient-to-br from-blue-600/20 to-transparent">
                    <Scales size={80} className="text-blue-500/50" />
                  </div>
                </div>
              </div>
              
              {/* Corner decorations */}
              <div className="absolute -top-2 -left-2 w-8 h-8 border-t border-l border-blue-500" />
              <div className="absolute -bottom-2 -right-2 w-8 h-8 border-b border-r border-blue-500" />
              
              {/* IA Badge */}
              <div className="absolute -bottom-4 -left-4 bg-blue-600 text-white px-4 py-2 font-mono text-sm font-medium">
                IA + DERECHO
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Marquee */}
      <div className="mt-8 marquee-container py-4">
        <Marquee speed={40} gradient={false}>
          {achievements.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-3 mx-8 font-mono text-sm text-white/50 uppercase tracking-wider"
            >
              <item.icon size={18} className="text-blue-500" weight="light" />
              <span>{item.text}</span>
              <span className="text-white/20 ml-4">/</span>
            </div>
          ))}
        </Marquee>
      </div>
    </section>
  );
};

export default Hero;
